// 负责购物车抽屉和 Mock checkout 表单，与购物车状态和订单创建流程关联。
import { Minus, Plus, ShoppingBag } from "lucide-react";
import { formatCurrency, type CartLine } from "../../domain/cart";
import type { CustomerDetails } from "../../domain/orders";

type CartPanelProps = {
  canCheckout: boolean;
  cartLines: CartLine[];
  checkoutError: string;
  checkoutTotal: number;
  customer: CustomerDetails;
  estimatedShipping: number;
  isCheckoutLoading: boolean;
  isOpen: boolean;
  subtotal: number;
  onCheckout: () => void;
  onClose: () => void;
  onCustomerChange: (customer: CustomerDetails) => void;
  onUpdateQuantity: (productId: string, size: string, change: number) => void;
};

export const CartPanel = ({
  canCheckout,
  cartLines,
  checkoutError,
  checkoutTotal,
  customer,
  estimatedShipping,
  isCheckoutLoading,
  isOpen,
  subtotal,
  onCheckout,
  onClose,
  onCustomerChange,
  onUpdateQuantity
}: CartPanelProps) => {
  return (
    <aside className={isOpen ? "cart-panel open" : "cart-panel"} aria-label="Cart">
      <div className="cart-header">
        <h2>Shopping bag</h2>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>

      {cartLines.length === 0 ? (
        <p className="empty-cart">Your bag is ready for the first test order.</p>
      ) : (
        <div className="cart-lines">
          {cartLines.map((line) => (
            <div className="cart-line" key={`${line.productId}-${line.size}`}>
              <img src={line.product.imageUrl} alt={line.product.name} />
              <div>
                <strong>{line.product.name}</strong>
                <span>Size {line.size}</span>
                <span>{formatCurrency(line.lineTotal)}</span>
              </div>
              <div className="quantity-control" aria-label="Quantity controls">
                <button
                  type="button"
                  aria-label={`Decrease ${line.product.name}`}
                  onClick={() => onUpdateQuantity(line.productId, line.size, -1)}
                >
                  <Minus aria-hidden="true" size={15} />
                </button>
                <span>{line.quantity}</span>
                <button
                  type="button"
                  aria-label={`Increase ${line.product.name}`}
                  onClick={() => onUpdateQuantity(line.productId, line.size, 1)}
                >
                  <Plus aria-hidden="true" size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="cart-summary">
        <label className="checkout-field">
          <span>Email</span>
          <input
            type="email"
            value={customer.email}
            onChange={(event) =>
              onCustomerChange({
                ...customer,
                email: event.target.value
              })
            }
            placeholder="buyer@example.com"
          />
        </label>
        <label className="checkout-field">
          <span>Ship to</span>
          <select
            value={customer.country}
            onChange={(event) =>
              onCustomerChange({
                ...customer,
                country: event.target.value
              })
            }
          >
            <option>United States</option>
            <option>Canada</option>
            <option>United Kingdom</option>
            <option>Australia</option>
            <option>Singapore</option>
          </select>
        </label>
        <div>
          <span>Subtotal</span>
          <strong>{formatCurrency(subtotal)}</strong>
        </div>
        <div>
          <span>Estimated shipping</span>
          <strong>
            {estimatedShipping === 0 ? "Free" : formatCurrency(estimatedShipping)}
          </strong>
        </div>
        <div>
          <span>Total</span>
          <strong>{formatCurrency(checkoutTotal)}</strong>
        </div>
        <button
          className="primary-button"
          type="button"
          disabled={!canCheckout || isCheckoutLoading}
          onClick={onCheckout}
        >
          <ShoppingBag aria-hidden="true" size={19} />
          {isCheckoutLoading ? "Starting checkout" : "Mock checkout"}
        </button>
        {checkoutError ? (
          <p className="setup-note error">{checkoutError}</p>
        ) : (
          <p className="setup-note">
            Mock checkout creates an order without processing real money.
          </p>
        )}
      </div>
    </aside>
  );
};
